// store.ts
import { ENTRY_BASE_FRAGMENT } from '@/gql/fragments/entries.gql';
import { createFragmentParser } from '@liquorice/gql-utils';
import { EventCardFragment } from '@/__generated__/graphql';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface ShortlistState extends ShortlistActions {
  events: EventCardFragment[] | null;
  latestEvent: EventCardFragment | null;
  notification: boolean;
}

interface ShortlistActions {
  setEvents: (events: EventCardFragment[] | null) => void;
  addEvent: (event: EventCardFragment) => void;
  removeEvent: (eventId: string) => void;
  clearEvents: () => void;
  toggleEvent: (event: EventCardFragment) => void;
  setNotification: (notification: boolean) => void;
}

export const useShortlistStore = create<ShortlistState>()(
  persist(
    (set) => ({
      events: [],
      latestEvent: null,
      notification: false,
      setEvents: (events: EventCardFragment[] | null) => set({ events }),
      addEvent: (newEvent: EventCardFragment) => {
        set((state) => {
          const updatedEvents = [...(state.events || []), newEvent];
          return {
            events: updatedEvents,
            latestEvent: newEvent,
            notification: true,
          };
        });
      },
      removeEvent: (eventId: string) => {
        set((state) => {
          const getBaseData = createFragmentParser(ENTRY_BASE_FRAGMENT, (data) => data);
          const updatedEvents = state.events?.filter((e) => getBaseData(e)?.id !== eventId) ?? null;
          return {
            events: updatedEvents,
            latestEvent: null,
            notification: false,
          };
        });
      },
      clearEvents: () => {
        set({
          events: null,
          latestEvent: null,
          notification: false,
        });
      },
      toggleEvent: (event: EventCardFragment) => {
        set((state) => {
          const getBaseData = createFragmentParser(ENTRY_BASE_FRAGMENT, (data) => data);
          const isEventPresent = state.events?.some(
            (e) => getBaseData(e)?.id === getBaseData(event)?.id
          );
          const updatedEvents = isEventPresent
            ? state.events?.filter((e) => getBaseData(e)?.id !== getBaseData(event)?.id) ?? null
            : [...(state.events || []), event];
          return {
            events: updatedEvents,
            latestEvent: !isEventPresent
              ? event
              : updatedEvents
              ? updatedEvents[updatedEvents.length - 1]
              : null,
            notification: !isEventPresent,
          };
        });
      },
      setNotification: (notification: boolean) => set({ notification }),
    }),
    {
      name: 'shortlist-storage', // name of item in the storage (must be unique)
      storage: createJSONStorage(() => sessionStorage), // (optional) by default the 'localStorage' is used
      partialize: (state) => ({ events: state.events, latestEvent: state.latestEvent }),
      // onRehydrateStorage: (state) => {
      //   console.log('hydration starts');

      //   // optional
      //   return (state, error) => {
      //     if (error) {
      //       console.log('an error happened during hydration', error);
      //     } else {
      //       console.log('hydration finished');
      //     }
      //   };
      // },
    }
  )
);

export const useShortlistCount = (state: ShortlistState) => state.events?.length ?? 0;

export const useShortlistLength = (state: ShortlistState) => !!state.events?.length;

export const useIsShortlisted = (card: EventCardFragment) => {
  const { events } = useShortlistStore();

  const getBaseData = createFragmentParser(ENTRY_BASE_FRAGMENT, (data) => data);

  return events?.some((e) => getBaseData(e)?.id === getBaseData(card)?.id) ?? false;
};

export default useShortlistStore;
